import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ChevronSmallLeft, ChevronSmallRight} from '@ELib/assets/images';

import {EIconButton} from '../../EButtons';

import {ListsContainer, SwitchButtonBox} from './ColumnsCustomizeDialog.styles';
import {
  isActiveOrInactiveString,
  SortElement,
} from './ColumnsCustomizeDialog.types';
import {ColumnOptions} from './SortableColumnsList';

// Code largly based and taken from https://github.com/WawanC/multi-column-dnd-kit/
export const ColumnSelection = ({
  internalItems,
  setInternalItems,
}: {
  internalItems: Record<'active' | 'inactive', SortElement[]>;
  setInternalItems: (
    setValue:
      | ((
          currentItems: Record<'active' | 'inactive', SortElement[]>
        ) => Record<'active' | 'inactive', SortElement[]>)
      | Record<'active' | 'inactive', SortElement[]>
  ) => void;
}): React.ReactElement => {
  const {t} = useTranslation('lib');
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 0.5,
      },
    })
  );

  const [selectedItemId, setSelectedItemId] = useState<string>();

  const moveActiveDisabled =
    !selectedItemId ||
    internalItems.active.some((item) => item.id === selectedItemId);
  const moveInactiveDisabled =
    !selectedItemId ||
    internalItems.inactive.some((item) => item.id === selectedItemId);

  const moveItemBetweenLists = (
    itemIdToMove?: string,
    listToRemove?: SortElement[],
    listToAdd?: SortElement[]
  ): {listWithRemoved?: SortElement[]; listWithAdded?: SortElement[]} => {
    const selected = listToRemove?.find((item) => item.id === itemIdToMove);
    if (!listToAdd || !listToRemove || !selected || !itemIdToMove) {
      return {listWithAdded: listToAdd, listWithRemoved: listToRemove};
    }
    return {
      listWithAdded: [...listToAdd, selected],
      listWithRemoved: listToRemove.filter((item) => item.id !== itemIdToMove),
    };
  };

  // function to handle button click to switch inactive column to active columns
  const handleMakeActive = () => {
    if (moveActiveDisabled) {
      return;
    }
    setInternalItems((itemList) => {
      const {listWithAdded, listWithRemoved} = moveItemBetweenLists(
        selectedItemId,
        itemList.inactive,
        itemList.active
      );

      return {inactive: listWithRemoved ?? [], active: listWithAdded ?? []};
    });
  };

  // function to handle button click to switch active column to inactive columns
  const handleMakeInactive = () => {
    if (moveInactiveDisabled) {
      return;
    }
    setInternalItems((itemList) => {
      const {listWithAdded, listWithRemoved} = moveItemBetweenLists(
        selectedItemId,
        itemList.active,
        itemList.inactive
      );

      return {active: listWithRemoved ?? [], inactive: listWithAdded ?? []};
    });
  };

  /**
   * Called when item is dropped by the dnd-kit internal logic
   * In this case we can switch in the dropped container itself
   * As switching betwen containers is handled in the handleDragOver handler
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const originContainerId = (
      event.active.data.current?.sortable as {containerId?: string} | undefined
    )?.containerId;
    const destinationContainerId = (
      event.over?.data.current?.sortable as {containerId?: string} | undefined
    )?.containerId;

    // Check if item is drag into unknown area or position is unchanged
    if (
      !event.over ||
      !event.active.data.current ||
      !event.over.data.current ||
      event.active.id === event.over.id ||
      !isActiveOrInactiveString(originContainerId) ||
      !destinationContainerId ||
      originContainerId !== destinationContainerId
    ) {
      return;
    }

    setInternalItems((itemList) => {
      const temp = {...itemList};
      const containerItems = temp[originContainerId];
      const overItem = event.over?.id;

      if (!overItem) {
        return temp;
      }

      const oldIdx = containerItems.findIndex(
        (item) => item.id === event.active.id
      );
      const newIdx = containerItems.findIndex((item) => item.id === overItem);

      // Sort the items list order based on item target position
      temp[originContainerId] = arrayMove(containerItems, oldIdx, newIdx);
      return temp;
    });
  };

  /**
   * handle switch between containers when the element gets dragged over to other container and its elements
   */
  const handleDragOver = (e: DragOverEvent) => {
    if (!e.over) {
      return;
    }

    const initialContainer = (
      e.active.data.current?.sortable as {containerId?: string} | undefined
    )?.containerId;
    const targetContainer = (
      e.over.data.current?.sortable as {containerId?: string} | undefined
    )?.containerId;

    // if there are none initial sortable list name, then item is not sortable item
    if (!isActiveOrInactiveString(initialContainer)) {
      return;
    }

    // Order the item list based on target item position
    setInternalItems((itemList) => {
      const temp = {...itemList};
      const initialContainerItems = temp[initialContainer];

      const draggedItem = initialContainerItems.find(
        (item) => item.id === e.active.id
      );

      if (!draggedItem) {
        return temp;
      }

      // If there are no target container then item is moved into a droppable zone
      // droppable = whole area of the sortable list (works when the sortable list is empty)
      if (!targetContainer) {
        if (!isActiveOrInactiveString(e.over?.id)) {
          return temp;
        }

        // If item is already there then don't re-added it or if base element does not exist
        if (itemList[e.over.id].some((item) => item.id === e.active.id)) {
          return temp;
        }

        // Remove item from it's initial container
        temp[initialContainer] = initialContainerItems.filter(
          (item) => item.id !== e.active.id
        );

        // Add item to it's target container which the droppable zone belongs to
        temp[e.over.id].push(draggedItem);
        return temp;
      }

      // If the item is drag around in the same container then just reorder the list
      if (initialContainer === targetContainer) {
        const oldIdx = initialContainerItems.findIndex(
          (item) => item.id === e.active.id
        );
        const newIdx = initialContainerItems.findIndex(
          (item) => item.id === e.over!.id
        );

        temp[initialContainer] = arrayMove(
          initialContainerItems,
          oldIdx,
          newIdx
        );
      } else {
        // If the item is drag into another different container

        const overId = e.over?.id;
        if (!isActiveOrInactiveString(targetContainer)) {
          return temp;
        }
        // Remove item from it's initial container
        temp[initialContainer] = initialContainerItems.filter(
          (item) => item.id !== e.active.id
        );

        // Add item to it's target container
        const newIdx = temp[targetContainer].findIndex(
          (item) => item.id === overId
        );
        temp[targetContainer].splice(newIdx, 0, draggedItem);
      }

      return temp;
    });
  };

  return (
    <DndContext
      collisionDetection={closestCorners}
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <ListsContainer>
        <ColumnOptions
          columnItems={internalItems.active}
          label={t('columnsDialog.active')}
          name='active'
          selectedItemId={selectedItemId}
          setSelectedItemId={setSelectedItemId}
        />
        <SwitchButtonBox>
          <EIconButton
            disabled={moveInactiveDisabled}
            testId='makeColumnInactive'
            title={t('columnsDialog.actions.makeInactive')}
            onClick={handleMakeInactive}
          >
            <ChevronSmallRight />
          </EIconButton>
          <EIconButton
            disabled={moveActiveDisabled}
            title={t('columnsDialog.actions.makeActive')}
            onClick={handleMakeActive}
          >
            <ChevronSmallLeft />
          </EIconButton>
        </SwitchButtonBox>
        <ColumnOptions
          columnItems={internalItems.inactive}
          label={t('columnsDialog.inactive')}
          name='inactive'
          selectedItemId={selectedItemId}
          setSelectedItemId={setSelectedItemId}
        />
      </ListsContainer>
    </DndContext>
  );
};
